/**
 * useSIPFilters Hook
 *
 * Manages all filter state and handlers for the Storm Impact Predictions page.
 * Extracts filter logic from SIPApp for better separation of concerns.
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { trackRumAction } from "@/lib/datadog";
import { format, isAfter, isValid, startOfDay } from "date-fns";
import {
  useGeographicHierarchies,
  useStormImpactModels,
  useDisplayableApplications,
  useAvailableRuns,
  useGeographicNodesWithGeometry,
  useForecastData,
  createForecastDataMap,
} from "@/hooks/api";
import { parseISO } from "date-fns";
import type {
  StormImpactModel,
  WeatherModel,
  StormImpactVariable,
  GeographicNode,
  GeographicHierarchy,
} from "@/types";
import {
  ForecastDataItem,
  ForecastDataByNodeId,
  ForecastAggregationDTO,
  ForecastVariableData,
  GeographicNodeWithGeometry,
  GeographicNodesParams,
} from "@/api/types";
import type { ApiError } from "@/api/client";

export interface UseSIPFiltersReturn {
  // API Data
  geographicHierarchy: GeographicHierarchy | null;
  stormImpactModels: StormImpactModel[];
  geographicNodesData: GeographicNodeWithGeometry[] | null;
  forecastData: ForecastDataItem[] | null;
  forecastDataMap: ForecastDataByNodeId;
  forecastAggregation: ForecastAggregationDTO | null; // Parent node aggregation (total row)
  availableRuns: string[];
  runHourMap: Record<string, string>;

  // Loading States
  isFiltersLoading: boolean;
  isMapDataLoading: boolean;
  isGeoDataLoading: boolean; // True while geographic geometry is loading (map can't render)
  isForecastDataLoading: boolean; // True while forecast data is loading (map colors / table data)
  isForecastPlaceholder: boolean; // True when forecast data is stale placeholder from a previous query
  isModelRunSelectionPending: boolean; // True while a model switch is waiting for a valid run selection
  isNoForecastFound: boolean; // True when forecast request completed with no rows for selected date/run
  geographicHierarchiesLoading: boolean;
  stormModelsLoading: boolean;
  weatherModelsLoading: boolean;
  geographicNodesLoading: boolean;
  availableRunsLoading: boolean;
  forecastLoading: boolean;
  dateLoading: boolean;

  // Errors
  geographicHierarchiesError: ApiError | null;
  stormModelsError: ApiError | null;
  geographicNodesError: ApiError | null;
  availableRunsError: ApiError | null;
  forecastError: ApiError | null;

  // Selected Values
  selectedModel: StormImpactModel | undefined;
  selectedWeatherModel: WeatherModel | undefined;
  selectedVariable: StormImpactVariable | undefined;
  selectedDate: Date;
  selectedRun: Date | undefined;
  selectedGeoNode: GeographicNode | undefined;

  // Derived Data
  availableWeatherModels: WeatherModel[];
  availableVariables: StormImpactVariable[];
  forecastNodeIds: string;

  // Handlers
  handleModelSelect: (model: StormImpactModel) => void;
  handleWeatherModelSelect: (model: WeatherModel) => void;
  handleVariableSelect: (variable: StormImpactVariable) => void;
  handlePropertySelect: (propertyId: string) => void;
  handleDateChange: (date: Date) => void;
  handleRunSelect: (run: Date) => void;
  handleGeoUnitChange: (node: GeographicNode) => void;
}

export interface UseSIPFiltersOptions {
  appSlug?: string;
}

export function useSIPFilters(options?: UseSIPFiltersOptions): UseSIPFiltersReturn {
  const appSlug = options?.appSlug ?? "sip";

  const getPreferredVariable = useCallback(
    (variables: StormImpactVariable[]): StormImpactVariable | undefined => {
      if (!variables.length) return undefined;
      return (
        variables.find((v) => v.variable_slug === "best_estimate") ||
        variables[0]
      );
    },
    [],
  );

  const getPreferredWeatherModel = useCallback(
    (models: WeatherModel[]): WeatherModel | undefined => {
      if (!models.length) return undefined;
      return (
        models.find((wm) => wm.slug === "global-forecast-system") ||
        models.find((wm) => wm.acronym?.toUpperCase() === "GFS") ||
        models[0]
      );
    },
    [],
  );

  // ============================================================================
  // API Hooks - Fetch data in parallel on page load
  // ============================================================================
  const {
    data: geographicHierarchiesData,
    loading: geographicHierarchiesLoading,
    error: geographicHierarchiesError,
  } = useGeographicHierarchies();

  const { data: applications } = useDisplayableApplications();
  const applicationId = useMemo(() => {
    if (applications?.length && appSlug) {
      const app = applications.find((a) => a.slug === appSlug);
      if (app?.id) return app.id;
    }
    return typeof window !== "undefined" ? localStorage.getItem("current_application_id") : null;
  }, [applications, appSlug]);

  const {
    data: stormModelsData,
    loading: stormModelsLoading,
    error: stormModelsError,
  } = useStormImpactModels(applicationId ?? undefined);

  const [geoNodeParams, setGeoNodeParams] = useState<GeographicNodesParams>();

  const {
    data: geographicNodesData,
    loading: geographicNodesLoading,
    error: geographicNodesError,
  } = useGeographicNodesWithGeometry(geoNodeParams);

  // ============================================================================
  // Selection State
  // ============================================================================
  const [selectedModel, setSelectedModel] = useState<
    StormImpactModel | undefined
  >(undefined);

  const [selectedWeatherModel, setSelectedWeatherModel] = useState<
    WeatherModel | undefined
  >(undefined);
  const [selectedVariable, setSelectedVariable] = useState<
    StormImpactVariable | undefined
  >(undefined);

  const [selectedDate, setSelectedDate] = useState<Date>(() => startOfDay(new Date()));
  const [selectedRun, setSelectedRun] = useState<Date | undefined>(undefined);
  const [selectedGeoNode, setSelectedGeoNode] = useState<
    GeographicNode | undefined
  >(undefined);

  const [hasInitializedDate, setHasInitializedDate] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [hasFetchedForecastOnce, setHasFetchedForecastOnce] = useState(false);
  const [lastSelectedGeoNodeId, setLastSelectedGeoNodeId] = useState<string | null>(null);
  const [pendingModelRunSelectionForId, setPendingModelRunSelectionForId] =
    useState<string | null>(null);

  // Compute geographic node IDs for forecast API (MUST be before API hooks that use it)
  const forecastNodeIds = useMemo(() => {
    if (!selectedGeoNode) return "";
    
    // If virtual root, return all its children (which are the actual roots)
    if (selectedGeoNode.id === "org-root") {
      return selectedGeoNode.trees?.map((n) => n.id).join(",") || "";
    }

    // If node has children, return children IDs (standard drill-down behavior)
    if (selectedGeoNode.trees && selectedGeoNode.trees.length > 0) {
      return selectedGeoNode.trees.map((n) => n.id).join(",");
    }
    
    // If no children, return the node's own ID
    return selectedGeoNode.id;
  }, [selectedGeoNode]);

  useEffect(() => {
    if (selectedGeoNode && selectedGeoNode.id !== lastSelectedGeoNodeId) {
      setSelectedRun(undefined);
      setLastSelectedGeoNodeId(selectedGeoNode.id);
    }
  }, [selectedGeoNode, lastSelectedGeoNodeId]);

  const currentDateStr = format(selectedDate, "yyyy-MM-dd");

  const {
    data: dateSpecificRunsData,
    loading: dateSpecificRunsLoading,
    error: availableRunsError,
  } = useAvailableRuns({
    stormImpactModelId: hasInitializedDate ? selectedModel?.model_id : undefined,
    weatherModelId: hasInitializedDate ? selectedWeatherModel?.id : undefined,
    forecastDate: hasInitializedDate ? currentDateStr : undefined,
  });

  const availableRunsData = dateSpecificRunsData;
  const availableRunsLoading = dateSpecificRunsLoading;

  // Transform available runs to string array
  const availableRuns: string[] = useMemo(() => {
    if (!availableRunsData) return [];
    return availableRunsData.map((run) => run.run);
  }, [availableRunsData]);

  // Map of run ISO string -> run_hour for display (e.g. "18" for 18Z)
  const runHourMap: Record<string, string> = useMemo(() => {
    if (!availableRunsData) return {};
    const map: Record<string, string> = {};
    availableRunsData.forEach((run) => {
      if (run.run_hour) {
        map[run.run] = run.run_hour;
      }
    });
    return map;
  }, [availableRunsData]);

  // ============================================================================
  // Forecast Data API - triggered when run is selected or geo changes
  // ============================================================================
  const {
    data: forecastData,
    aggregation: forecastAggregation,
    loading: forecastLoading,
    error: forecastError,
    isPlaceholderData: isForecastPlaceholder,
  } = useForecastData({
    modelId: selectedModel?.model_id,
    weatherModelId: selectedWeatherModel?.id,
    geographicNodeIds: forecastNodeIds,
    forecast_date: format(selectedDate, "yyyy-MM-dd"),
    run: selectedRun?.toISOString(),
    aggregationNodeId: selectedGeoNode?.id === "org-root" ? undefined : selectedGeoNode?.id, 
  });

  // Create a map of geographic_node_id -> forecast data for quick lookups
  const forecastDataMap: ForecastDataByNodeId = useMemo(() => {
    return createForecastDataMap(forecastData);
  }, [forecastData]);

  // ============================================================================
  // Derived data from API responses
  // ============================================================================

  const geographicHierarchy = useMemo(() => {
    if (!geographicHierarchiesData || geographicHierarchiesData.length === 0) {
      return null;
    }
    return geographicHierarchiesData[0];
  }, [geographicHierarchiesData]);

  const stormImpactModels = useMemo(() => {
    return stormModelsData || [];
  }, [stormModelsData]);

    const availableWeatherModels = useMemo(() => {
    return selectedModel?.weather_models || [];
  }, [selectedModel?.weather_models]);

  const availableVariables = useMemo(() => {
    const modelVariables = selectedModel?.variables || [];
    const forecastEntries = Object.values(forecastDataMap || {});

    const sortByCatalogOrder = (vars: StormImpactVariable[]) =>
      [...vars].sort((a, b) => {
        const orderA = a.variable_order ?? 999;
        const orderB = b.variable_order ?? 999;
        if (orderA !== orderB) return orderA - orderB;
        return a.variable_slug.localeCompare(b.variable_slug);
      });

    const variableValuesBySlug = new Map<string, ForecastVariableData>();
    for (const entry of forecastEntries) {
      for (const fv of entry?.variable_values ?? []) {
        if (
          (fv.variable_type === "model" || fv.variable_type === "weather" || fv.variable_type === "threat") &&
          !variableValuesBySlug.has(fv.variable_slug)
        ) {
          variableValuesBySlug.set(fv.variable_slug, fv);
        }
      }
    }

    if (variableValuesBySlug.size === 0) {
      return sortByCatalogOrder(modelVariables);
    }

    const modelBySlug = new Map(
      (modelVariables || []).map((v) => [v.variable_slug, v]),
    );

    const variables = Array.from(variableValuesBySlug.values()).map((fv) => {
      const modelVar = modelBySlug.get(fv.variable_slug);
      return {
        variable_id: fv.variable_id || modelVar?.variable_id || fv.variable_slug,
        variable_name: fv.variable_name || modelVar?.variable_name || fv.variable_slug,
        variable_slug: fv.variable_slug,
        variable_type: String(fv.variable_type || "model").toLowerCase(),
        // Prefer model catalog order over forecast payload order so new DB rows respect storm_impact_variables.variable_order.
        variable_order: modelVar?.variable_order ?? fv.variable_order ?? 999,
        variable_units: fv.variable_units || modelVar?.variable_units || "",
        variable_summary: modelVar?.variable_summary || fv.variable_summary || "",
        variable_description: modelVar?.variable_description || fv.variable_description || "",
        bins: modelVar?.bins || [],
      } as StormImpactVariable;
    });

    return sortByCatalogOrder(variables);
  }, [selectedModel?.variables, forecastDataMap]);

  // ============================================================================
  // Loading states
  // ============================================================================

  const isFiltersLoading =
    !isInitialLoadComplete &&
    (geographicHierarchiesLoading ||
      stormModelsLoading ||
      !hasInitializedDate);

  // Geographic geometry loading - blocks the map from rendering at all
  const isGeoDataLoading = !isInitialLoadComplete
    ? geographicNodesLoading || !geographicNodesData
    : geographicNodesLoading;

  // Forecast data loading - only blocks forecast coloring / table data, NOT the map shell.
  // Stay true until forecast API completes and we have data (so map/table are populated).
  const isForecastDataLoading = !isInitialLoadComplete
    ? !hasFetchedForecastOnce || forecastLoading
    : forecastLoading ||
      availableRunsLoading ||
      (!selectedRun &&
        availableRuns.length > 0 &&
        hasInitializedDate &&
        !!selectedModel &&
        !!selectedWeatherModel);

  const isNoForecastFound =
    !isFiltersLoading &&
    !isGeoDataLoading &&
    !availableRunsLoading &&
    !!selectedModel &&
    !!selectedWeatherModel &&
    !!selectedRun &&
    !forecastLoading &&
    !forecastError &&
    availableRuns.length > 0 &&
    (forecastData?.length ?? 0) === 0;

  // Combined map data loading (kept for backward compat, but now only used as a union)
  const isMapDataLoading = isGeoDataLoading || isForecastDataLoading;

  const isWeatherModelsLoading =
    stormModelsLoading ||
    (!selectedModel && stormImpactModels.length === 0);

  const isAvailableRunsLoading =
    availableRunsLoading ||
    !hasInitializedDate ||
    isWeatherModelsLoading ||
    (!selectedWeatherModel && availableWeatherModels.length === 0);

  const initialSelectedDate = useMemo(() => {
    const today = startOfDay(new Date());
    const maxForecastDate = selectedWeatherModel?.max_forecast_date
      ? startOfDay(parseISO(selectedWeatherModel.max_forecast_date))
      : undefined;

    if (!maxForecastDate || !isValid(maxForecastDate)) {
      return today;
    }

    return isAfter(maxForecastDate, today) ? today : maxForecastDate;
  }, [selectedWeatherModel?.max_forecast_date]);

  // ============================================================================
  // Helper function to fetch geographic nodes for a given node
  // ============================================================================
  const fetchNodesForGeoNode = useCallback((node: GeographicNode) => {
    // Handle virtual root
    if (node.id === "org-root" && node.trees && node.trees.length > 0) {
      // Find the most common resolution among roots to fetch geometry
      const resolutions = node.trees.map(n => n.resolution);
      const commonResolution = resolutions.sort((a,b) =>
          resolutions.filter(v => v===a).length
        - resolutions.filter(v => v===b).length
      ).pop();
      
      const ids = node.trees.map((n) => n.id).join(",");
      setGeoNodeParams({ resolution: commonResolution || "district", id: ids });
      return;
    }

    if (node.trees && node.trees.length > 0) {
      const resolution = node.trees[0].resolution;
      const ids = node.trees.map((n) => n.id).join(",");
      setGeoNodeParams({ resolution, id: ids });
    } else {
      // Fetch the node itself if it has no children
      setGeoNodeParams({ resolution: node.resolution, id: node.id });
    }
  }, []);

  // ============================================================================
  // Effects - Set defaults when data loads
  // ============================================================================

  // Initialize the selected date on first load:
  // use today when the weather model supports dates beyond today,
  // otherwise clamp to the weather model's max forecast date.
  useEffect(() => {
    if (hasInitializedDate || !selectedWeatherModel) return;

    setSelectedDate(initialSelectedDate);
    setHasInitializedDate(true);
  }, [hasInitializedDate, initialSelectedDate, selectedWeatherModel]);

  // Set default storm model when data loads
  useEffect(() => {
    if (stormImpactModels.length > 0 && !selectedModel) {
      const firstModel = stormImpactModels[0];
      setSelectedModel(firstModel);

      if (firstModel.variables && firstModel.variables.length > 0) {
        setSelectedVariable(getPreferredVariable(firstModel.variables));
      }
    }
  }, [stormImpactModels, selectedModel, getPreferredVariable]);

  // Set default weather model when availableWeatherModels changes
  useEffect(() => {
    if (availableWeatherModels.length > 0) {
      const isSelectedValid =
        selectedWeatherModel &&
        availableWeatherModels.some((wm) => wm.id === selectedWeatherModel.id);

      if (!isSelectedValid) {
        setSelectedWeatherModel(getPreferredWeatherModel(availableWeatherModels));
      }
    } else if (!stormModelsLoading && selectedModel && selectedWeatherModel) {
      setSelectedWeatherModel(undefined);
    }
  }, [
    availableWeatherModels,
    selectedWeatherModel,
    stormModelsLoading,
    selectedModel,
    getPreferredWeatherModel,
  ]);

  useEffect(() => {
    if (availableVariables.length === 0) {
      setSelectedVariable(undefined);
      return;
    }
    const selectedId = selectedVariable?.variable_id;
    const isStillAvailable = selectedId && availableVariables.some((v) => v.variable_id === selectedId);
    if (!isStillAvailable) {
      setSelectedVariable(getPreferredVariable(availableVariables));
    }
  }, [availableVariables, selectedVariable?.variable_id, getPreferredVariable]);

  // Auto-fetch geographic nodes when hierarchy first loads
  useEffect(() => {
    if (geographicHierarchy && !selectedGeoNode) {
      // If there are multiple root trees, we want to start at the virtual "org-root"
      // otherwise start at the single root tree.
      if (geographicHierarchy.trees && geographicHierarchy.trees.length > 1) {
        const virtualRoot: GeographicNode = {
          id: "org-root",
          name: geographicHierarchy.hierarchy_name,
          slug: geographicHierarchy.hierarchy_slug,
          resolution: "Organization",
          selectable: true,
          trees: geographicHierarchy.trees,
        } as GeographicNode;
        setSelectedGeoNode(virtualRoot);
        fetchNodesForGeoNode(virtualRoot);
      } else {
        const rootNode = geographicHierarchy.trees?.[0];
        if (rootNode) {
          setSelectedGeoNode(rootNode);
          fetchNodesForGeoNode(rootNode);
        }
      }
    }
  }, [geographicHierarchy, selectedGeoNode, fetchNodesForGeoNode]);

  // Set default run when available runs change
  useEffect(() => {
    if (!availableRunsData || availableRunsData.length === 0) {
      setSelectedRun(undefined);
      return;
    }

    if (selectedRun) {
      const selectedTime = selectedRun.getTime();
      const stillAvailable = availableRunsData.some(
        (r) => new Date(r.run).getTime() === selectedTime,
      );
      if (stillAvailable) return;
    }

    setSelectedRun(new Date(availableRunsData[0].run));
  }, [availableRunsData, selectedRun]);

  useEffect(() => {
    if (
      !pendingModelRunSelectionForId ||
      selectedModel?.model_id !== pendingModelRunSelectionForId
    ) {
      return;
    }

    if (availableRunsLoading) {
      return;
    }

    if (!selectedWeatherModel) {
      if (availableWeatherModels.length === 0) {
        setPendingModelRunSelectionForId(null);
      }
      return;
    }

    const isSelectedWeatherModelValid = availableWeatherModels.some(
      (weatherModel) => weatherModel.id === selectedWeatherModel.id,
    );
    if (!isSelectedWeatherModelValid) {
      return;
    }

    if (!availableRunsData || availableRunsData.length === 0) {
      setPendingModelRunSelectionForId(null);
      return;
    }

    if (!selectedRun) {
      return;
    }

    const selectedTime = selectedRun.getTime();
    const hasValidRun = availableRunsData.some(
      (run) => new Date(run.run).getTime() === selectedTime,
    );

    if (hasValidRun) {
      setPendingModelRunSelectionForId(null);
    }
  }, [
    availableRunsData,
    availableRunsLoading,
    availableWeatherModels,
    pendingModelRunSelectionForId,
    selectedModel?.model_id,
    selectedWeatherModel,
    selectedRun,
  ]);

  useEffect(() => {
    if (
      !hasFetchedForecastOnce &&
      !forecastLoading &&
      selectedModel &&
      selectedWeatherModel &&
      selectedRun &&
      forecastNodeIds
    ) {
      setHasFetchedForecastOnce(true);
    }
  }, [
    forecastLoading,
    hasFetchedForecastOnce,
    selectedModel,
    selectedWeatherModel,
    selectedRun,
    forecastNodeIds,
  ]);

  // Mark initial load complete once filters + geographic geometry are ready
  // This allows the map, table, and controls to render while forecast data still loads
  useEffect(() => {
    if (
      !isInitialLoadComplete &&
      !geographicHierarchiesLoading &&
      !stormModelsLoading &&
      selectedModel &&
      selectedWeatherModel &&
      hasInitializedDate &&
      geographicNodesData &&
      geographicNodesData.length > 0
    ) {
      setIsInitialLoadComplete(true);
    }
  }, [
    isInitialLoadComplete,
    geographicHierarchiesLoading,
    stormModelsLoading,
    selectedModel,
    selectedWeatherModel,
    hasInitializedDate,
    geographicNodesData,
  ]);

  // ============================================================================
  // Handler functions
  // ============================================================================

  const handleModelSelect = useCallback((model: StormImpactModel) => {
    trackRumAction('sip_model_selected', {
      model_id: model?.model_id,
      model_name: model?.model_name,
    });
    setPendingModelRunSelectionForId(model.model_id);
    setSelectedModel(model);

    if (model.variables && model.variables.length > 0) {
      setSelectedVariable(getPreferredVariable(model.variables));
    } else {
      setSelectedVariable(undefined);
    }
  }, [getPreferredVariable]);

  const handleWeatherModelSelect = useCallback((model: WeatherModel) => {
    setSelectedWeatherModel(model);
  }, []);

  const handleVariableSelect = useCallback((variable: StormImpactVariable) => {
    setSelectedVariable(variable);
  }, []);

  const handlePropertySelect = useCallback(
    (propertyId: string) => {
      const variable = availableVariables.find(
        (v) => v.variable_id === propertyId,
      );
      if (variable) {
        setSelectedVariable(variable);
      }
    },
    [availableVariables],
  );

  const handleDateChange = useCallback((date: Date) => {
    setSelectedDate(date);
    setSelectedRun(undefined);
  }, []);

  const handleRunSelect = useCallback((run: Date) => {
    setSelectedRun(run);
  }, []);

  const handleGeoUnitChange = useCallback(
    (node: GeographicNode) => {
      setSelectedGeoNode(node);
      fetchNodesForGeoNode(node);
    },
    [fetchNodesForGeoNode],
  );

  // ============================================================================
  // Return
  // ============================================================================
  return {
    // API Data
    geographicHierarchy: geographicHierarchy ?? null,
    stormImpactModels,
    geographicNodesData: geographicNodesData ?? null,
    forecastData: forecastData ?? null,
    forecastDataMap,
    forecastAggregation,
    availableRuns,
    runHourMap,

    // Loading States
    isFiltersLoading,
    isMapDataLoading,
    isGeoDataLoading,
    isForecastDataLoading,
    isForecastPlaceholder,
    isModelRunSelectionPending:
      pendingModelRunSelectionForId === selectedModel?.model_id,
    isNoForecastFound,
    geographicHierarchiesLoading,
    stormModelsLoading,
    weatherModelsLoading: isWeatherModelsLoading,
    geographicNodesLoading,
    availableRunsLoading: isAvailableRunsLoading,
    forecastLoading,
    dateLoading: !hasInitializedDate,

    // Errors
    geographicHierarchiesError,
    stormModelsError,
    geographicNodesError,
    availableRunsError,
    forecastError,

    // Selected Values
    selectedModel,
    selectedWeatherModel,
    selectedVariable,
    selectedDate,
    selectedRun,
    selectedGeoNode,

    // Derived Data
    availableWeatherModels,
    availableVariables,
    forecastNodeIds,

    // Handlers
    handleModelSelect,
    handleWeatherModelSelect,
    handleVariableSelect,
    handlePropertySelect,
    handleDateChange,
    handleRunSelect,
    handleGeoUnitChange,
  };
}
